import React from 'react';
import Modal from './Modal';
import { AlertTriangle, CheckCircle, Info, ChevronRight, Lightbulb } from 'lucide-react';
import {
    analyzeHeadings,
    HEADING_LEVEL_BORDER as LEVEL_COLORS,
    HEADING_LEVEL_TAG as LEVEL_TAG_COLORS,
} from '../utils/headingsAnalysis';

export default function HeadingsStructureModal({ open, onClose, headingsList = [], headingsCounts = {} }) {
    const { issues, strengths, contentRecs, hasIssues, hasWarnings } = analyzeHeadings(headingsList, headingsCounts);

    const totalHeadings = headingsList.length;

    return (
        <Modal open={open} onClose={onClose} title="Architecture des titres" size="xl">
            {/* Summary badges */}
            <div className="flex flex-wrap gap-2 mb-5">
                {Object.entries(headingsCounts)
                    .filter(([, count]) => count > 0)
                    .map(([level, count]) => (
                        <span key={level} className={`text-sm px-3 py-1.5 rounded-lg font-mono font-medium ${LEVEL_TAG_COLORS[parseInt(level[1])] || 'bg-blue-gray-100 text-blue-gray-700'}`}>
                            {level.toUpperCase()}: <strong>{count}</strong>
                        </span>
                    ))
                }
                <span className="text-sm px-3 py-1.5 rounded-lg bg-blue-gray-100 text-blue-gray-600">
                    Total: <strong>{totalHeadings}</strong>
                </span>
            </div>

            {/* Health status */}
            {!hasIssues && !hasWarnings && (
                <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-xl mb-5">
                    <CheckCircle size={18} className="text-green-600" />
                    <span className="text-green-700 font-medium text-sm">La structure des titres est correcte !</span>
                </div>
            )}

            {/* Structural issues */}
            {issues.length > 0 && (
                <div className="space-y-2 mb-5">
                    <div className="text-xs font-semibold text-blue-gray-500 uppercase tracking-wide mb-1">Problèmes structurels</div>
                    {issues.map((issue, i) => (
                        <div key={i} className={`p-3 rounded-xl border ${issue.severity === 'error' ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`}>
                            <div className="flex items-start gap-2">
                                <AlertTriangle size={16} className={`flex-shrink-0 mt-0.5 ${issue.severity === 'error' ? 'text-red-500' : 'text-amber-500'}`} />
                                <div>
                                    <div className={`text-sm font-medium ${issue.severity === 'error' ? 'text-red-800' : 'text-amber-800'}`}>
                                        {issue.message}
                                    </div>
                                    {issue.fix && (
                                        <div className="text-xs text-blue-gray-600 mt-1 flex items-center gap-1">
                                            <Info size={12} className="flex-shrink-0" />
                                            {issue.fix}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Content-aware recommendations */}
            {contentRecs.length > 0 && (
                <div className="space-y-2 mb-5">
                    <div className="text-xs font-semibold text-blue-gray-500 uppercase tracking-wide mb-1 flex items-center gap-1">
                        <Lightbulb size={12} /> Recommandations de contenu
                    </div>
                    {contentRecs.map((rec, i) => (
                        <div key={i} className="p-3 rounded-xl border bg-blue-50 border-blue-200">
                            <div className="flex items-start gap-2">
                                <Lightbulb size={16} className="flex-shrink-0 mt-0.5 text-blue-500" />
                                <div>
                                    <div className="text-sm font-medium text-blue-800">{rec.message}</div>
                                    <div className="text-xs text-blue-700 mt-1">
                                        💡 {rec.suggestion}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Strengths */}
            {strengths.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-5">
                    {strengths.map((s, i) => (
                        <span key={i} className="text-xs bg-green-50 text-green-700 border border-green-200 px-2.5 py-1 rounded-lg">
                            {s}
                        </span>
                    ))}
                </div>
            )}

            {/* Heading tree */}
            <div className="border border-blue-gray-200 rounded-xl overflow-hidden">
                <div className="bg-blue-gray-50 px-4 py-2.5 border-b border-blue-gray-200">
                    <span className="text-sm font-semibold text-blue-gray-700">Hiérarchie des titres</span>
                </div>
                {headingsList.length > 0 ? (
                    <div className="divide-y divide-blue-gray-100">
                        {headingsList.map((h, i) => {
                            const indent = (h.level - 1) * 20;
                            const levelColor = LEVEL_COLORS[h.level] || 'border-l-blue-gray-300';
                            return (
                                <div
                                    key={i}
                                    className={`flex items-center gap-2 py-2.5 px-4 border-l-4 ${levelColor} transition-colors hover:bg-blue-gray-50/80`}
                                    style={{ paddingLeft: `${16 + indent}px` }}
                                >
                                    {h.level > 1 && (
                                        <ChevronRight size={12} className="text-blue-gray-300 flex-shrink-0" />
                                    )}
                                    <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${LEVEL_TAG_COLORS[h.level] || 'bg-blue-gray-100 text-blue-gray-600'}`}>
                                        {h.tag.toUpperCase()}
                                    </span>
                                    <span className={`text-sm truncate ${h.level === 1 ? 'font-semibold text-blue-gray-900' : 'text-blue-gray-700'} ${!h.text ? 'italic text-blue-gray-300' : ''}`}>
                                        {h.text || '(vide)'}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="text-center py-8 text-blue-gray-400 text-sm">
                        Aucun titre détecté sur cette page.
                    </div>
                )}
            </div>

            {/* SEO Tips */}
            <div className="mt-5 p-4 bg-blue-gray-50 rounded-xl">
                <div className="text-xs font-semibold text-blue-gray-600 mb-2">💡 Vision Google & Utilisateur :</div>
                <ul className="text-xs text-blue-gray-600 space-y-1.5">
                    <li>• Googlebot lit les titres pour comprendre la <strong>structure thématique</strong> de votre page</li>
                    <li>• Un utilisateur qui parcourt la page doit comprendre le contenu en lisant <strong>uniquement les titres</strong></li>
                    <li>• Chaque H2 doit être un <strong>sous-thème distinct</strong> du H1</li>
                    <li>• Les H3 doivent <strong>détailler le H2 parent</strong>, pas introduire un nouveau sujet</li>
                    <li>• Incluez vos <strong>mots-clés</strong> dans les titres de manière naturelle</li>
                </ul>
            </div>
        </Modal>
    );
}
